// "Quote of the day" — one quote per day for everyone, so the portal has something human on it.
// The backend decides which quote a given Vietnam day gets and remembers it, so the card does not
// change when the page is refreshed and the whole company sees the same one.
//
// Two audiences: 'portal' (general drive) and 'developer' (Sample Tracker, aimed at product
// developers). Every quote is shown in English, then Vietnamese, then Korean. Vietnamese was added
// on 2026-08-19 at Max's request and sits directly under the English: the people who read this every
// morning are in Vietnam. Korean stays for Jay and Young. A translation that is missing is simply
// not rendered, so an older quote without a Vietnamese line still looks finished.
//
// Variants:
//   'line'  — the default. A slim strip that sits at the top of a working page, above its toolbar.
//             Jay asked for this on 2026-08-04: staff go straight to their work screen, so a quote
//             only on the home page is never seen, and the original card was far too tall.
//   'hero'  — the original tall card. Kept for anywhere a page wants a feature block.
//   'strip' — mid-size, between the two.
//   'header'— one language, two lines, in the portal header before the bell (2026-10-15). The pages
//             no longer carry the 'line' strip; it is kept for anywhere that wants one back.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Quote as QuoteIcon } from 'lucide-react';
import { BACKEND_URL } from './backendConfig';

/** "(1963– )" while living, "(1900–1993)" once they have died. */
const lifespanOf = (q) => {
  if (!q.birthYear) return '';
  return q.deathYear ? `(${q.birthYear}–${q.deathYear})` : `(${q.birthYear}– )`;
};

/** "Basketball player, USA · Chicago Bulls" — role and organisation, skipping whichever is absent. */
const affiliationOf = (title, org) => [title, org].map((v) => (v || '').trim()).filter(Boolean).join(' · ');

/* One answer per audience per day, kept for the life of the tab. The header variant lives in the
   Header, which remounts on every screen — without this the quote would be fetched again and pop in
   late on each navigation. Keyed by the day, so a tab left open overnight asks again. */
const quoteCache = new Map();
const dayKey = (audience) => `${audience}|${new Date().toDateString()}`;

const QuoteOfTheDay = ({ audience = 'portal', variant = 'line' }) => {
  const { t, i18n } = useTranslation();
  const cached = quoteCache.get(dayKey(audience));
  const [quote, setQuote] = useState(cached || null);
  const [state, setState] = useState(cached ? 'ready' : 'loading'); // loading | ready | empty | error

  useEffect(() => {
    const hit = quoteCache.get(dayKey(audience));
    if (hit) { setQuote(hit); setState('ready'); return undefined; }
    let alive = true;
    axios.get(`${BACKEND_URL}/api/portal/quote-of-the-day`, { params: { audience } })
      .then((res) => {
        if (!alive) return;
        if (res.data?.quote) {
          quoteCache.set(dayKey(audience), res.data.quote);
          setQuote(res.data.quote);
          setState('ready');
        } else {
          setState('empty');
        }
      })
      .catch(() => { if (alive) setState('error'); });
    return () => { alive = false; };
  }, [audience]);

  /* 'header' — Max, 2026-10-15: the quote moves out of the page into the header, just before the
     bell, the way the DAC board carries it ("Phần quote mang trên trước cái chuông giống cái của
     DAC á"), and in ONE language — the one the portal is in, following the language switch. The
     page strips printed English, Vietnamese and Korean stacked; the header has room for one.
     Two lines: the quote (one line, the rest on hover) and who said it. Nothing while loading,
     so the header never holds an empty box. */
  if (variant === 'header') {
    if (state !== 'ready' || !quote) return null;
    const lang = String(i18n.language || '');
    const ko = lang.startsWith('ko') && !!quote.quoteKo;
    // Vietnamese reads the quote's own Vietnamese line (2026-10-21), and Chinese its Chinese one (sql/20261021i);
    // a quote with no line in the reader's language reads English, whole, rather than half in each.
    const vi = lang.startsWith('vi') && !!quote.quoteVi;
    const zh = lang.startsWith('zh') && !!quote.quoteZh;
    // French and Japanese (04/11/2026) the same way
    const fr = lang.startsWith('fr') && !!quote.quoteFr;
    const ja = lang.startsWith('ja') && !!quote.quoteJa;
    const text = ko ? quote.quoteKo : vi ? quote.quoteVi : zh ? quote.quoteZh : fr ? quote.quoteFr : ja ? quote.quoteJa : quote.quoteEn;
    const who = ko ? (quote.authorKo || quote.author) : zh ? (quote.authorZh || quote.author)
      : fr ? (quote.authorFr || quote.author) : ja ? (quote.authorJa || quote.author) : quote.author;
    const affil = ko
      ? affiliationOf(quote.authorTitleKo, quote.orgKo || quote.orgEn)
      : vi
        ? affiliationOf(quote.authorTitleVi || quote.authorTitleEn, quote.orgVi || quote.orgEn)
        : zh
          ? affiliationOf(quote.authorTitleZh || quote.authorTitleEn, quote.orgZh || quote.orgEn)
          : fr
            ? affiliationOf(quote.authorTitleFr || quote.authorTitleEn, quote.orgFr || quote.orgEn)
            : ja
              ? affiliationOf(quote.authorTitleJa || quote.authorTitleEn, quote.orgJa || quote.orgEn)
              : affiliationOf(quote.authorTitleEn, quote.orgEn);
    const by = [`— ${who} ${lifespanOf(quote)}`.trim(), affil].filter(Boolean).join(' · ');
    return (
      <figure className={`qotd-header${ko ? ' qotd-header-ko' : ''}${vi ? ' qotd-header-vi' : ''}${zh ? ' qotd-header-zh' : ''}${fr ? ' qotd-header-vi' : ''}${ja ? ' qotd-header-zh' : ''}`} title={`\u201C${text}\u201D\n${by}`} aria-label={t('quote.label')}>
        <blockquote className="qotd-header-txt">{`\u201C${text}\u201D`}</blockquote>
        <figcaption className="qotd-header-by">{by}</figcaption>
      </figure>
    );
  }

  // Nothing to show is not an error worth a red box on a work screen — render nothing.
  if (state !== 'ready' || !quote) {
    if (state === 'loading') return <div className={`qotd qotd-${variant} qotd-loading`} aria-busy="true" />;
    return null;
  }

  const affiliationEn = affiliationOf(quote.authorTitleEn, quote.orgEn);
  // Only the hero/strip branch prints these two — the slim line names the author once, in English.
  const affiliationKo = affiliationOf(quote.authorTitleKo, quote.orgKo);
  // Company names are not translated, so the Vietnamese affiliation falls back to the English org.
  const affiliationVi = affiliationOf(quote.authorTitleVi, quote.orgVi || quote.orgEn);
  const label = t('quote.label');

  // The slim variant keeps everything on one line: label, quote, then the attribution inline.
  // A separate branch rather than CSS gymnastics on the tall layout.
  if (variant === 'line') {
    return (
      <figure className="qotd qotd-line">
        <span className="qotd-line-tag" title={label}>
          <QuoteIcon size={13} strokeWidth={2.6} />
        </span>
        <div className="qotd-line-body">
          {/* The English row is two parts: the quote, which may wrap to two lines and is cut after
              that, and the attribution, which sits OUTSIDE the cut so a long quote can never swallow
              the author's name (Max, 2026-08-19: "quote dài thì bị mất chữ"). */}
          <p className="qotd-line-en">
            <span className="qotd-line-txt">{quote.quoteEn}</span>
            <span className="qotd-line-by">
              — {quote.author}
              {affiliationEn ? `, ${affiliationEn}` : ''} {lifespanOf(quote)}
            </span>
          </p>
          {/* The name is printed once, on the English line. It used to repeat under the Vietnamese
              and Korean translations, which read as three different people (Jay, 2026-08-24). */}
          {quote.quoteVi && (
            <p className="qotd-line-vi">
              <span className="qotd-line-txt">{quote.quoteVi}</span>
            </p>
          )}
          {quote.quoteKo && (
            <p className="qotd-line-ko">
              <span className="qotd-line-txt">{quote.quoteKo}</span>
            </p>
          )}
        </div>
      </figure>
    );
  }

  return (
    <figure className={`qotd qotd-${variant}`}>
      <div className="qotd-label">
        <QuoteIcon size={variant === 'hero' ? 16 : 14} strokeWidth={2.5} />
        <span>{label}</span>
      </div>

      <blockquote className="qotd-en">{quote.quoteEn}</blockquote>
      {quote.quoteVi && <p className="qotd-vi">{quote.quoteVi}</p>}
      {quote.quoteKo && <p className="qotd-ko">{quote.quoteKo}</p>}

      <figcaption className="qotd-by">
        <span className="qotd-author">— {quote.author}</span>
        {affiliationEn && <span className="qotd-affil">{affiliationEn}</span>}
        {affiliationVi && <span className="qotd-affil qotd-affil-vi">{affiliationVi}</span>}
        {quote.authorKo && (
          <span className="qotd-affil qotd-affil-ko">
            {quote.authorKo}{affiliationKo ? ` · ${affiliationKo}` : ''}
          </span>
        )}
        <span className="qotd-years">{lifespanOf(quote)}</span>
      </figcaption>
    </figure>
  );
};

export default QuoteOfTheDay;
